/**
 * Shared utilities for bulk CSV upload/invite modals.
 */

export interface DownloadRow {
  row: number;
  reason?: string;
  valid?: boolean;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Generate and trigger a CSV download of the invalid rows from a bulk upload.
 * @param rows      All rows (valid + invalid) from the validate response
 * @param columns   Ordered list of data column keys to include (before "reason")
 * @param filename  The .csv filename to save as
 */
export const downloadErrorsCsv = (
  rows: DownloadRow[],
  columns: string[],
  filename: string,
): void => {
  const invalid = rows.filter((r) => r.valid === false);
  const allCols = ['row', ...columns, 'reason'];
  const header = allCols.join(',');
  const lines = invalid.map((r) =>
    allCols
      .map((col) => `"${String(r[col] ?? '').replace(/"/g, '""')}"`)
      .join(','),
  );
  const csv = [header, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/** Credential row surfaced after creating/activating users (single source of the CSV column contract). */
export interface CredentialRow {
  email: string;
  firstName: string;
  lastName: string;
  temporaryPassword: string;
  entraProvisioned: boolean;
}

const CREDENTIALS_HEADERS = ['email', 'firstName', 'lastName', 'temporaryPassword', 'entraProvisioned'];

/**
 * Download a credentials CSV (email, name, temp password, Entra status) for created/activated
 * users. The column contract lives here so the direct-create and bulk-activate flows stay in sync.
 */
export const downloadCredentialsCsv = (
  credentials: CredentialRow[],
  filename = 'user-credentials.csv',
): void => {
  downloadTemplateCsv(
    CREDENTIALS_HEADERS,
    credentials.map((c) => ({ ...c, entraProvisioned: String(c.entraProvisioned) })),
    filename,
  );
};

/** Triggers a browser download of a server-provided Blob (e.g. a streamed CSV export). */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadTemplateCsv = (
  headers: string[],
  sampleRows: Array<Record<string, string>>,
  filename: string,
): void => {
  const escapeCell = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const headerLine = headers.map(escapeCell).join(',');
  const lines = sampleRows.map((row) =>
    headers.map((header) => escapeCell(row[header] ?? '')).join(','),
  );
  const csv = [headerLine, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
